import { Popover } from "radix-ui";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "use-debounce";

import { CaretDownIcon, MagnifyingGlassIcon } from "@/modules/common/assets/icons";

import CheckboxV2 from "../CheckboxV2";
import EmptySelectList from "../EmptySelectList";
import { useModalPortalContainer } from "../Modal";
import { cn } from "@/libs/cn";
import { CheckIcon } from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SelectV2Option {
  value: string;
  label: string;
  description?: string;
}

interface SelectV2BaseProps {
  options?: SelectV2Option[];

  /** Label rendered above the trigger button */
  label?: React.ReactNode;
  /** Text shown inside the trigger when nothing is selected */
  placeholder?: string;
  /** Placeholder inside the search input */
  searchPlaceholder?: string;

  /** Called with the debounced search string whenever it changes */
  onSearch?(query: string): void;
  /** Debounce delay in ms (default: 500) */
  debounceMs?: number;

  /** Whether the options list is currently loading */
  isLoading?: boolean;
  /** Whether there are more pages to load */
  hasMore?: boolean;
  /** Called when the scroll sentinel becomes visible */
  onLoadMore?(): void;

  disabled?: boolean;
  /** Max items selectable in multiple mode */
  maxSelected?: number;
  /** Exibe asterisco vermelho no label */
  markAsRequired?: boolean;

  className?: string;
  triggerClassName?: string;
  /** Ícone ou elemento exibido antes do texto no trigger */
  triggerStartIcon?: React.ReactNode;
  /** Alinhamento do painel em relação ao trigger (default: "start") */
  align?: "start" | "center" | "end";

  /** Custom option renderer */
  renderOption?(option: SelectV2Option, isSelected: boolean): React.ReactNode;
  /** Custom renderer for the selected value displayed inside the trigger */
  renderTriggerSelected?(option: SelectV2Option): React.ReactNode;
  /** Content rendered when the list is empty and not loading */
  emptyContent?: React.ReactNode;
}

type SelectV2SingleProps = {
  mode: "single";
  value?: string;
  onChange?(value: string | undefined): void;
};

type SelectV2MultipleProps = {
  mode?: "multiple";
  value?: string[];
  onChange?(value: string[]): void;
};

export type SelectV2Props = (SelectV2SingleProps | SelectV2MultipleProps) & SelectV2BaseProps;

// ---------------------------------------------------------------------------
// Internal: infinite-scroll container
// ---------------------------------------------------------------------------

interface ScrollContainerProps {
  children: React.ReactNode;
  hasMore?: boolean;
  isLoading?: boolean;
  onLoadMore?(): void;
}

const ScrollContainer = ({ children, hasMore, isLoading, onLoadMore }: ScrollContainerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasMore || isLoading) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onLoadMore?.();
      },
      { root: containerRef.current, rootMargin: "0px", threshold: 0.8 },
    );

    const sentinel = sentinelRef.current;
    if (sentinel) observer.observe(sentinel);
    return () => {
      if (sentinel) observer.unobserve(sentinel);
    };
  }, [hasMore, isLoading, onLoadMore]);

  return (
    <div ref={containerRef} className="max-h-65 overflow-y-auto">
      {children}
      <div ref={sentinelRef} className="h-1" />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const SelectV2 = (props: SelectV2Props) => {
  const {
    options = [],
    label,
    placeholder = "Selecione",
    searchPlaceholder = "Buscar",
    onSearch,
    debounceMs = 500,
    isLoading = false,
    hasMore = false,
    onLoadMore,
    disabled = false,
    maxSelected,
    markAsRequired,
    className,
    triggerClassName,
    triggerStartIcon,
    align = "start",
    renderOption,
    renderTriggerSelected,
    emptyContent,
  } = props;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, debounceMs);
  const inputRef = useRef<HTMLInputElement>(null);
  const modalPortalContainer = useModalPortalContainer();

  // Clear search when the popover closes
  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  // Forward debounced search to parent
  useEffect(() => {
    onSearch?.(debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Filtragem local quando onSearch não é fornecido
  const visibleOptions = useMemo(() => {
    if (onSearch) return options;
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, debouncedSearch, onSearch]);

  // ---------------------------------------------------------------------------
  // Selection helpers (mode-aware)
  // ---------------------------------------------------------------------------

  const isMultiple = props.mode !== "single";

  const isSelected = (optValue: string): boolean => {
    if (isMultiple) {
      return ((props as SelectV2MultipleProps).value ?? []).includes(optValue);
    }
    return (props as SelectV2SingleProps).value === optValue;
  };

  const handleSelect = (optValue: string) => {
    if (isMultiple) {
      const multiProps = props as SelectV2MultipleProps;
      const current = multiProps.value ?? [];
      const max = maxSelected ?? Infinity;

      if (current.includes(optValue)) {
        multiProps.onChange?.(current.filter((v) => v !== optValue));
      } else if (current.length < max) {
        multiProps.onChange?.([...current, optValue]);
      }
    } else {
      const singleProps = props as SelectV2SingleProps;
      const current = singleProps.value;
      singleProps.onChange?.(current === optValue ? undefined : optValue);
      setOpen(false);
    }
  };

  const handleClear = () => {
    if (isMultiple) {
      const multiProps = props as SelectV2MultipleProps;
      multiProps.onChange?.([]);
    }
  };

  // ---------------------------------------------------------------------------
  // Trigger label
  // ---------------------------------------------------------------------------

  const triggerLabel = (): string => {
    if (isMultiple) {
      const selected = (props as SelectV2MultipleProps).value ?? [];
      if (selected.length === 0) return placeholder;
      if (selected.length === 1) {
        const label = options.find((o) => o.value === selected[0])?.label ?? placeholder;
        return label;
      }
      return `${selected.length} selecionados`;
    } else {
      const val = (props as SelectV2SingleProps).value;
      if (!val) return placeholder;
      const label = options.find((o) => o.value === val)?.label ?? placeholder;
      return label;
    }
  };

  const triggerFullLabel = (): string => {
    if (isMultiple) {
      const selected = (props as SelectV2MultipleProps).value ?? [];
      if (selected.length === 1) return options.find((o) => o.value === selected[0])?.label ?? "";
      return "";
    }
    const val = (props as SelectV2SingleProps).value;
    return val ? (options.find((o) => o.value === val)?.label ?? "") : "";
  };

  const hasSelection = isMultiple ? ((props as SelectV2MultipleProps).value ?? []).length > 0 : !!(props as SelectV2SingleProps).value;
  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className={cn("flex min-w-0 flex-col gap-0.5", className)}>
      {label && (
        <span className="text-sm">
          {label}
          {markAsRequired && <span className="text-red-500"> *</span>}
        </span>
      )}

      <Popover.Root open={open} onOpenChange={disabled ? undefined : setOpen}>
        {/* Trigger */}
        <Popover.Trigger
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          onFocus={() => !disabled && setOpen(true)}
          className={cn(
            "min-h-10 w-full text-sm border border-ui-border-muted rounded-md px-3 py-2 bg-white",
            "flex items-center justify-between gap-2 text-left select-none",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-ui-primary/50",
            "data-[state=open]:border-ui-primary cursor-pointer",
            disabled && "bg-[#F5F5F5] text-[#A3A3A3] cursor-not-allowed opacity-60",
            triggerClassName,
          )}
        >
          {triggerStartIcon != null && <span className="shrink-0 flex items-center">{triggerStartIcon}</span>}

          {renderTriggerSelected && !isMultiple && (props as SelectV2SingleProps).value ? (
            <div className="flex-1 min-w-0">
              {renderTriggerSelected(options.find((o) => o.value === (props as SelectV2SingleProps).value)!)}
            </div>
          ) : (
            <span
              className={cn("flex-1 truncate", hasSelection ? "text-ui-text" : "text-ui-text-muted")}
              title={triggerFullLabel() || undefined}
            >
              {triggerLabel()}
            </span>
          )}
          <CaretDownIcon className={cn("shrink-0 text-ui-text-muted transition-transform duration-200", open && "rotate-180")} />
        </Popover.Trigger>

        {/* Content panel */}
        <Popover.Portal container={modalPortalContainer}>
          <Popover.Content
            align={align}
            sideOffset={4}
            role="listbox"
            aria-multiselectable={isMultiple}
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              inputRef.current?.focus();
            }}
            className={cn("z-50 bg-white rounded-xl shadow-lg border border-ui-border", "w-90", "animate-in fade-in-0 zoom-in-95")}
          >
            {/* Search bar */}
            <div className="p-3 border-b border-ui-border">
              <div className="flex items-center gap-2 rounded-md border border-ui-border px-3 py-2 bg-white">
                <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-ui-text-muted" />
                <input
                  type="text"
                  value={search}
                  ref={inputRef}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  className={cn("flex-1 text-sm text-ui-text bg-transparent outline-none", "placeholder:text-ui-text-muted")}
                />
              </div>
              {isMultiple && (
                <button
                  type="button"
                  className="flex justify-center text-ui-text-muted rounded-md border border-ui-border mt-2 bg-white w-full transition-colors hover:border-ui-primary hover:text-ui-text"
                  onClick={handleClear}
                >
                  <p>Limpar seleção</p>
                </button>
              )}
            </div>

            {/* Options list */}
            <ScrollContainer hasMore={hasMore} isLoading={isLoading} onLoadMore={onLoadMore}>
              <div className="p-2 flex flex-col gap-0.5">
                {!isLoading && visibleOptions.length === 0 && <>{emptyContent ?? <EmptySelectList />}</>}

                {visibleOptions.map((option) => {
                  const selected = isSelected(option.value);

                  return (
                    <div
                      key={option.value}
                      role="option"
                      aria-selected={selected}
                      onClick={() => handleSelect(option.value)}
                      className={cn(
                        "flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer",
                        "hover:bg-ui-hover transition-colors duration-100",
                        selected && "bg-ui-hover",
                      )}
                    >
                      {renderOption ? (
                        renderOption(option, selected)
                      ) : (
                        <>
                          <div className="flex flex-col min-w-0 mr-3">
                            <span className="text-sm font-medium text-ui-text select-none truncate" title={option.label}>
                              {option.label}
                            </span>
                            {option.description && (
                              <span className="text-xs text-ui-text-muted select-none truncate" title={option.description}>
                                {option.description}
                              </span>
                            )}
                          </div>

                          {isMultiple ? (
                            <CheckboxV2 size="sm" checked={selected} tabIndex={-1} className="pointer-events-none" />
                          ) : (
                            <CheckIcon
                              className={cn("h-4 w-4 text-ui-primary transition-opacity", selected ? "opacity-100" : "opacity-0")}
                            />
                          )}
                        </>
                      )}
                    </div>
                  );
                })}

                {isLoading && <p className="text-sm text-center text-ui-text-muted py-4">Carregando...</p>}
              </div>
            </ScrollContainer>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};

export default SelectV2;
